import type { AuthUser } from '@proref/types';
import type {
  LoginInput,
  SignupInput,
  ForgotPasswordInput,
  ResetPasswordInput,
  ChangePasswordInput,
} from '@proref/schemas';
import { api, unwrap, type Envelope } from '../lib/api';
import { clearTokens, getTokens, setTokens, type AuthTokens } from '../lib/token-store';

export async function login(input: LoginInput): Promise<void> {
  const res = await api.post<Envelope<AuthTokens>>('/auth/login', input);
  setTokens(unwrap(res.data));
}

// Start a password reset. The API always responds 202 (no account enumeration), so there's
// no payload to read — success just means the request was accepted.
export async function forgotPassword(input: ForgotPasswordInput): Promise<void> {
  await api.post('/auth/forgot-password', input);
}

// Complete a password reset with the emailed token. Throws on an invalid/expired/used token.
export async function resetPassword(input: ResetPasswordInput): Promise<void> {
  await api.post('/auth/reset-password', input);
}

// Change your own password from /settings. Distinct from resetPassword above: that one redeems an
// emailed token, this one proves you know the current password. Succeeding also signs out every
// OTHER device server-side, which is why the settings copy says so.
export async function changePassword(input: ChangePasswordInput): Promise<void> {
  await api.post('/me/password', input);
}

// Redeem an invite: role/federation/sport come from the invite row server-side, never the client.
export async function signup(input: SignupInput): Promise<void> {
  const res = await api.post<Envelope<AuthTokens>>('/auth/signup', input);
  setTokens(unwrap(res.data));
}

export async function logout(): Promise<void> {
  const tokens = getTokens();
  // Best-effort server-side revoke; clear locally regardless so the user always lands signed out.
  if (tokens?.refresh_token) {
    try {
      await api.post('/auth/logout', { refresh_token: tokens.refresh_token });
    } catch {
      // Network/revoke failure shouldn't trap the user in a logged-in shell.
    }
  }
  clearTokens();
}

export async function getMe(): Promise<AuthUser> {
  const res = await api.get<Envelope<AuthUser>>('/me');
  return unwrap(res.data);
}

// Whether a persisted session exists — lets the auth bootstrap skip the /me call when logged out.
export function hasSession(): boolean {
  return getTokens() !== null;
}
